import { createBrowserRouter } from 'react-router';

import { ROUTES } from '~shared/routes';
import { AuthenticatedLayout } from '~widgets/authenticated-layout';

import { GlobalErrorFallback } from './components/GlobalErrorFallback';
import { AdminGuard, AuthGuard, ValwinAdminGuard } from './components/guards';
import { Loading } from './components/Loading';
import {
  career,
  extranet,
  promotionCode,
  promotions,
  valwinAdmin,
} from './routes';

export const router = createBrowserRouter([
  {
    path: ROUTES.login,
    errorElement: <GlobalErrorFallback />,
    HydrateFallback: () => <Loading />,
    children: [
      // Public routes
      {
        index: true,
        lazy: async () => {
          const { LoginPage } = await import('../pages/login/LoginPage');

          return { Component: LoginPage };
        },
      },
      {
        path: ROUTES.resetPassword,
        lazy: async () => {
          const { ResetPasswordPage } =
            await import('../pages/reset-password/ResetPasswordPage');

          return { Component: ResetPasswordPage };
        },
      },
      {
        path: ROUTES.forgottenPassword,
        lazy: async () => {
          const { ForgottenPasswordPage } =
            await import('../pages/forgotten-password/ForgottenPasswordPage');

          return { Component: ForgottenPasswordPage };
        },
      },
      {
        path: ROUTES.registerQuery,
        lazy: async () => {
          const { RegisterQueryPage } =
            await import('../pages/register-query/RegisterQueryPage');

          return { Component: RegisterQueryPage };
        },
      },

      // Authenticated routes
      {
        Component: AuthGuard,
        children: [
          {
            Component: AuthenticatedLayout,
            children: [
              // User routes (no admin check)
              ...promotions,
              ...promotionCode,
              {
                path: ROUTES.mags,
                lazy: async () => {
                  const { MagsPage } = await import('../pages/mags');

                  return { Component: MagsPage };
                },
              },
              {
                path: ROUTES.profile,

                lazy: async () => {
                  const { Profile } = await import('../pages/profile');

                  return { Component: Profile };
                },
              },

              {
                path: ROUTES.forum,
                lazy: async () => {
                  const { IntranetPage } = await import('../pages/intranet');

                  return { Component: IntranetPage };
                },
              },
              {
                path: ROUTES.calendar,
                lazy: async () => {
                  const { HappeningPage } = await import('../pages/happening');

                  return { Component: HappeningPage };
                },
              },

              // Admin routes — AccessDenied rendered in place (with menu) if not admin
              {
                Component: AdminGuard,
                children: [
                  {
                    path: ROUTES.pharmacies,
                    lazy: async () => {
                      const { Pharmacies } =
                        await import('../pages/pharmacies');

                      return { Component: Pharmacies };
                    },
                  },

                  {
                    path: ROUTES.pharmaciesAffiliations,
                    lazy: async () => {
                      const { PharmaciesAffiliationPage } =
                        await import('../pages/pharmacies-affiliation');

                      return { Component: PharmaciesAffiliationPage };
                    },
                  },
                  {
                    path: ROUTES.blog.root,
                    lazy: async () => {
                      const { ArticlesPage } =
                        await import('../pages/articles');

                      return { Component: ArticlesPage };
                    },
                  },
                  {
                    path: ROUTES.blog.edit,
                    lazy: async () => {
                      const { ArticleFormPage } =
                        await import('../pages/articles');

                      return { Component: ArticleFormPage };
                    },
                  },

                  {
                    path: ROUTES.settings,
                    lazy: async () => {
                      const { Settings } = await import('../pages/settings');

                      return { Component: Settings };
                    },
                  },

                  {
                    path: ROUTES.campaigns.root,
                    lazy: async () => {
                      const { CampaignPage } =
                        await import('../pages/campaign');

                      return { Component: CampaignPage };
                    },
                  },
                  {
                    path: ROUTES.campaigns.form,
                    lazy: async () => {
                      const { CampaignFormPage } =
                        await import('../pages/campaign-form');

                      return { Component: CampaignFormPage };
                    },
                  },
                  {
                    path: ROUTES.campaigns.detail,
                    lazy: async () => {
                      const { CampaignDetailsPage } =
                        await import('../pages/campaign-details');

                      return { Component: CampaignDetailsPage };
                    },
                  },
                  {
                    path: ROUTES.campaigns.report,
                    lazy: async () => {
                      const { CampaignRapportPage } =
                        await import('../pages/campaign-rapport');

                      return { Component: CampaignRapportPage };
                    },
                  },
                  ...career,
                  ...extranet,
                  {
                    path: ROUTES.communication,
                    lazy: async () => {
                      const { CommunicationPage } =
                        await import('../pages/communication');

                      return { Component: CommunicationPage };
                    },
                  },
                  {
                    path: ROUTES.staticContents,

                    lazy: async () => {
                      const { PharmacyGroupAssetsPage } =
                        await import('../pages/pharmacy-group-assets');

                      return { Component: PharmacyGroupAssetsPage };
                    },
                  },
                  {
                    path: ROUTES.valwinNews,
                    lazy: async () => {
                      const { ValwinNewsPage } =
                        await import('../pages/valwin-news');

                      return { Component: ValwinNewsPage };
                    },
                  },
                ],
              },

              // Valwin admin routes — independent of group admin status

              {
                Component: ValwinAdminGuard,
                children: valwinAdmin,
              },

              // 404 inside layout (menu visible)
              {
                path: '*',
                lazy: async () => {
                  const { NotFoundPage } =
                    await import('../pages/not-found/NotFound.page');

                  return { Component: NotFoundPage };
                },
              },
            ],
          },
        ],
      },
    ],
  },

  // Global 404 (outside /:group context)
  {
    path: '*',
    lazy: async () => {
      const { NotFoundPage } = await import('../pages/not-found/NotFound.page');

      return { Component: NotFoundPage };
    },
  },
]);
